import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const items = [
  {
    q: "What is MyAdventure-Passport?",
    a: "A cheerful digital passport for campers. Every stamp is verified by the campsite owner, so your book only ever holds real adventures.",
  },
  {
    q: "How do stamps get issued?",
    a: "When you check in at a participating campsite, you generate a QR from the app. The owner scans it, confirms your stay, and your unique hexagonal stamp lands in your passport with +100 XP.",
  },
  {
    q: "Is the free plan really free?",
    a: "Yes — forever. You get the passport, unlimited stamp collecting, and the basic explore feed. Premium adds detailed stats, adventure map, journaling, and no ads.",
  },
  {
    q: "Where do you operate?",
    a: "We're launching in Malaysia and expanding campsite by campsite. If your favourite spot isn't listed, drop us a note from the Contact page.",
  },
  {
    q: "I run a campsite — how do I join?",
    a: "Head to our Contact page and pick the 'Campsite Owner' subject. We'll onboard you personally into the Basic (free) tier and walk you through custom stamps and analytics.",
  },
  {
    q: "When does the app launch?",
    a: "iOS and Android beta invites open with Batch III. Sign up to the newsletter at the trailhead to get first dibs on the app store rollout.",
  },
  {
    q: "Do you share my data?",
    a: "No. Your journey is yours. Full details on the Privacy Policy page.",
  },
];

function Item({ item, open, onToggle, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: i * 0.04 }}
      className="rounded-2xl overflow-hidden"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border-c)",
      }}
      data-testid={`faq-item-${i}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left px-6 py-5 gap-4"
        aria-expanded={open}
      >
        <span className="text-base md:text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>
          {item.q}
        </span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
          style={{ color: "var(--brand)" }}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-1 text-[15px] leading-relaxed" style={{ color: "var(--on-surface-muted)" }}>
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <>
      <section
        className="pt-[120px] md:pt-[140px] pb-10 text-center"
        style={{ background: "var(--surface-secondary)" }}
      >
        <div className="wrap">
          <div className="eyebrow mb-4">FAQ</div>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4" style={{ color: "var(--on-surface)" }}>
            Frequently asked questions
          </h1>
          <p className="body-lg max-w-2xl mx-auto">
            Everything you might want to know before you pack your bag.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="wrap max-w-3xl mx-auto space-y-4">
          {items.map((it, i) => (
            <Item
              key={it.q}
              item={it}
              i={i}
              open={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>
      </section>
    </>
  );
}
